import { TextButton as WSRTextButton } from '@wix/design-system'
import { ChoiceQuestionContentComponent, useContent, useMetadata, useNext } from '@wix/funnel-builder-lizard'
import { Button, TextButton } from '@wix/wildcat-ui'
import { ArrowRight, ChevronLeft } from '@wix/wix-ui-icons-common'
import classNames from 'classnames'
import React, { FC, useCallback, useEffect, useState } from 'react'

import { usePageLogger } from '../../../bi/mfi'
import { createMetadataFromBusinessTerm } from '../../../category/categoryInMetadata'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { useExperiments } from '../../../hooks/useExperiments'
import { Button as HarmonyButton } from '../../reusable/Button'
import type { BusinessTermService } from '../../reusable/Category/api/BusinessTermService'
import type { SearchSource } from '../../reusable/Category/state/types'
import { DesignSystemTokensLoader } from '../../reusable/DesignSystemTokensLoader'
import WixLogo from '../../reusable/icons/WixLogo.svg'
import { Input } from '../../reusable/Input/Input'
import { RadioOption } from '../../reusable/RadioOption'
import { getSectorAppIds } from '../../WixMobile/WixMobileApps/common'
import { businessTermMap } from './businessTermMap'
import { dataHooks } from './EasierCategory.dataHooks'
import s from './EasierCategory.scss'
import { matchBusinessTermFromText } from './matchBusinessTermFromText'

type OptionContent = {
  text: string
}

export type Content = {
  title: string
  skipButton: string
  nextButton: string
  video: string
  safariSrc?: string
  skin?: 'default' | 'harmony'
  otherInputLabel: string
  otherInputPlaceholder: string
  question: ChoiceQuestionContentComponent<{}, OptionContent>
}

const OTHER_INPUT_MAX_LENGTH = 80

export const EasierCategory: FC<{ businessTermService: BusinessTermService }> = ({ businessTermService }) => {
  const [selectedOptionKey, setSelectedOptionKey] = useState<string | null>(null)
  const [clickedButton, setClickedButton] = useState<string>()
  const [otherInputValue, setOtherInputValue] = useState('')

  const { content } = useContent<Content>()
  const {
    title,
    skipButton,
    nextButton,
    video: contentVideo,
    safariSrc,
    otherInputLabel,
    otherInputPlaceholder,
    question,
  } = content
  const { questionKey, options: allOptions } = question
  const options = allOptions.filter(o => o.optionKey in businessTermMap)
  const isHarmony = content.skin === 'harmony'

  const next = useNext()
  const pageLogger = usePageLogger()
  const { save } = useStorageContext()
  const experiments = useExperiments()
  const [{ language }] = useMetadata()

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const handleOptionClick = useCallback(
    (optionKey: string) => {
      if (optionKey === selectedOptionKey) {
        return
      }
      void pageLogger.clickAnswer({ questionKey, buttonName: optionKey })
      setSelectedOptionKey(optionKey)
    },
    [selectedOptionKey, pageLogger, questionKey],
  )

  const handleNextClick = useCallback(async () => {
    if (clickedButton || !selectedOptionKey) {
      return
    }
    setClickedButton('primary')

    const selectedIndex = options.findIndex(o => o.optionKey === selectedOptionKey)
    const entry = businessTermMap[selectedOptionKey]
    if (!entry) {
      console.error(`No business term mapping found for option key: ${selectedOptionKey}`)
      return
    }

    const isOther = selectedOptionKey === 'other'
    const freeText = isOther && otherInputValue ? otherInputValue : undefined
    const presetAppDefIds = entry.appDefId ? [entry.appDefId] : []

    const matched = freeText ? await matchBusinessTermFromText({ businessTermService, text: freeText, language }) : null

    const { businessTerm, businessSegmentId } = matched ?? entry
    const appDefIds = matched ? getSectorAppIds(businessSegmentId, experiments) : presetAppDefIds
    const search = freeText && matched ? freeText : ''
    const searchSource: SearchSource = matched ? 'input' : 'select'

    const metadata = createMetadataFromBusinessTerm(businessTerm, businessSegmentId)

    save({
      category: {
        search,
        searchSource,
        businessTerm,
        businessSegmentId,
        index: selectedIndex,
      },
      appDefIds,
    })

    void pageLogger.answerCategory({
      businessTerm,
      segment: metadata.segment,
      businessSegmentId,
      userSearchInput: freeText,
    })

    void pageLogger.answer({
      answer: businessTerm,
      position: selectedIndex + 1,
      answerType: isOther ? 'free text' : undefined,
      freeText,
    })

    const answerValues = businessTerm.displayName ? [businessTerm.displayName] : []
    next({ [questionKey]: { values: answerValues } }, metadata)
  }, [
    clickedButton,
    selectedOptionKey,
    options,
    pageLogger,
    questionKey,
    next,
    save,
    otherInputValue,
    experiments,
    language,
    businessTermService,
  ])

  const handleSkipClick = useCallback(() => {
    if (clickedButton) {
      return
    }
    setClickedButton('skip')

    void pageLogger.generalAction({ action: 'skip' })

    next({ [questionKey]: { skipped: true } })
  }, [clickedButton, pageLogger, next, questionKey])

  const handleBackClick = useCallback(() => {
    if (clickedButton) {
      return
    }
    setClickedButton('back')
    void pageLogger.generalAction({ action: 'back' })
    window.history.back()
  }, [clickedButton, pageLogger])

  return (
    <DesignSystemTokensLoader type="odeditor">
      <div className={classNames(s.root, { [s.rootHarmony]: isHarmony })} data-hook={dataHooks.template()}>
        <header className={s.header}>
          <button className={s.backBtn} onClick={handleBackClick}>
            <ChevronLeft />
          </button>
          <hr className={s.divider} />
          <WixLogo width={46} height={18} />
        </header>

        <div className={s.content}>
          <div className={s.left}>
            <div className={s.card}>
              <div className={s.cardContent}>
                <h1 className={s.title} data-hook={dataHooks.title()}>
                  {title}
                </h1>

                <div className={s.options}>
                  {options.map((option, index) => (
                    <RadioOption
                      key={option.optionKey}
                      name={questionKey}
                      value={option.optionKey}
                      checked={option.optionKey === selectedOptionKey}
                      onChange={() => handleOptionClick(option.optionKey)}
                      label={option.text}
                      dataHook={dataHooks.radio({ index })}
                      className={s.radioOption}
                    />
                  ))}
                </div>

                {selectedOptionKey === 'other' && (
                  <div className={s.otherInputBlock} data-hook={dataHooks.otherInputBlock()}>
                    <span className={s.otherInputLabel}>{otherInputLabel}</span>
                    <Input
                      type="text"
                      autoFocus
                      maxLength={OTHER_INPUT_MAX_LENGTH}
                      value={otherInputValue}
                      onChange={e => setOtherInputValue(e.target.value)}
                      placeholder={otherInputPlaceholder}
                      data-hook={dataHooks.otherInput()}
                      skin={isHarmony ? 'harmony' : 'standard'}
                    />
                  </div>
                )}

                {isHarmony ? (
                  <HarmonyButton
                    className={s.nextButton}
                    size="medium"
                    skin="standard"
                    onClick={handleNextClick}
                    disabled={!selectedOptionKey}
                    isLoading={clickedButton === 'primary'}
                    dataHook={dataHooks.nextButton()}
                    label={nextButton}
                  />
                ) : (
                  <Button
                    className={s.nextButton}
                    size="medium"
                    onClick={handleNextClick}
                    disabled={!selectedOptionKey}
                    isLoading={clickedButton === 'primary'}
                    dataHook={dataHooks.nextButton()}
                  >
                    {nextButton}
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className={s.right}>
            <div className={s.videoContainer}>
              {contentVideo && (
                <video className={s.video} autoPlay muted playsInline>
                  {safariSrc && <source src={safariSrc} type='video/quicktime; codecs="hvc1"' />}
                  <source src={contentVideo} />
                </video>
              )}
            </div>
          </div>
        </div>

        <div className={s.skip}>
          {isHarmony ? (
            <WSRTextButton skin="dark" size="small" onClick={handleSkipClick} suffixIcon={<ArrowRight />}>
              {skipButton}
            </WSRTextButton>
          ) : (
            <TextButton onClick={handleSkipClick} suffixIcon={<ArrowRight />}>
              {skipButton}
            </TextButton>
          )}
        </div>
      </div>
    </DesignSystemTokensLoader>
  )
}
